// Service pour la gestion des périodes de calcul

import { getStoreNames, getAllItems, saveItem, deleteItem } from './dbService';

const PERIODS_STORE = getStoreNames().PERIODS;

/**
 * Charge toutes les périodes
 * @returns {Promise<Array>} Liste des périodes
 */
export async function loadPeriods() {
  return await getAllItems(PERIODS_STORE);
}

/**
 * Ajoute une nouvelle période
 * @param {Object} period - La période à ajouter
 * @returns {Promise<Object>} La période ajoutée
 */
export async function addPeriod(period) {
  // S'assurer que la période a un ID
  if (!period.id) {
    period.id = Date.now();
  }

  // Utiliser une copie de l'objet pour éviter des modifications par référence
  const periodToSave = { ...period };

  // Sauvegarder la période et retourner l'objet sauvegardé
  return await saveItem(PERIODS_STORE, periodToSave);
}

/**
 * Met à jour une période existante
 * @param {Object} period - La période avec les modifications
 * @returns {Promise<Object>} La période mise à jour
 */
export async function updatePeriod(period) {
  return await saveItem(PERIODS_STORE, period);
}

/**
 * Supprime une période
 * @param {number} id - L'ID de la période à supprimer
 * @returns {Promise<boolean>} Vrai si la suppression a réussi
 */
export async function deletePeriod(id) {
  return await deleteItem(PERIODS_STORE, id);
}

/**
 * Vérifie si une période est la période par défaut (la plus ancienne)
 * @param {number} id - L'ID de la période à vérifier
 * @returns {Promise<boolean>} Vrai si c'est la période par défaut
 */
export async function isDefaultPeriod(id) {
  const periods = await loadPeriods();
  if (periods.length === 0) return false;

  // La période par défaut n'existe plus, toutes les périodes peuvent être supprimées
  return false;
}

/**
 * Vérifie si une période chevauche des périodes existantes
 * @param {Object} period - La période à vérifier
 * @param {number} excludeId - ID de la période à exclure de la vérification (pour les modifications)
 * @returns {Promise<boolean>} Vrai si la période chevauche une autre période
 */
export async function periodOverlaps(period, excludeId = null) {
  const periods = await loadPeriods();

  const startDate = new Date(period.startDate);
  const endDate = new Date(period.endDate);

  // Vérifier le chevauchement avec les périodes existantes
  return periods.some(existingPeriod => {
    // Exclure la période en cours de modification
    if (excludeId !== null && existingPeriod.id === excludeId) {
      return false;
    }

    const existingStartDate = new Date(existingPeriod.startDate);
    const existingEndDate = new Date(existingPeriod.endDate);

    // Il y a chevauchement si:
    // - le début de la période est entre le début et la fin d'une période existante
    // - la fin de la période est entre le début et la fin d'une période existante
    // - la période englobe entièrement une période existante
    return (startDate >= existingStartDate && startDate <= existingEndDate) ||
           (endDate >= existingStartDate && endDate <= existingEndDate) ||
           (startDate <= existingStartDate && endDate >= existingEndDate);
  });
}
